import { getCloudFrontAssetUrl } from '@utils/asset-utils';
import type { StageId } from '@micro-screens/src/types/stage-theme';
import { STAGE_THEME_COLORS } from '@micro-screens/theme/colors';

import './instructions-screen.css';

const STAGE_OVERLAYS: Record<StageId, string> = {
  1: getCloudFrontAssetUrl('stage-1/Stage_1.png'),
  2: getCloudFrontAssetUrl('stage-2/Stage_2.png'),
  3: getCloudFrontAssetUrl('stage-3/Stage_3.png'),
  4: getCloudFrontAssetUrl('stage-4/Stage_4.png'),
  5: getCloudFrontAssetUrl('stage-5/Stage_5.png'),
  6: getCloudFrontAssetUrl('stage-6/Stage_6.png'),
  7: getCloudFrontAssetUrl('stage-7/Stage_7.png'),
};

const SHIMMER_DARK = 'rgba(255,255,255,0.10)';
const SHIMMER_MID = 'rgba(255,255,255,0.15)';

interface GameInstructionsSkeletonProps {
  stage: StageId;
}

export function GameInstructionsSkeleton({ stage }: Readonly<GameInstructionsSkeletonProps>) {
  const theme = STAGE_THEME_COLORS[stage];
  const overlay = STAGE_OVERLAYS[stage];

  return (
    <div className="instructionViewport">
      <section
        className="instructionRoot"
        style={{ backgroundColor: theme.background }}
        aria-busy="true"
        aria-label="Loading game instructions"
      >
        <img src={overlay} alt="" aria-hidden className="overlay" />
        <div
          className="eclipse"
          style={{ '--eclipse-color': theme.eclipse } as React.CSSProperties}
        />

        <div className="content">
          {/* game-header */}
          <div className="game-header">
            <div className="metaRow">
              <div
                className="skeleton-pulse"
                style={{ width: 90, height: 12, borderRadius: 4, background: SHIMMER_MID }}
              />
              <div
                className="skeleton-pulse"
                style={{ width: 63, height: 20, borderRadius: 999, background: SHIMMER_DARK }}
              />
            </div>
            <div
              className="skeleton-pulse"
              style={{
                width: '52%',
                height: 20,
                marginTop: 10,
                borderRadius: 4,
                background: SHIMMER_MID,
              }}
            />
          </div>

          {/* screenHeading */}
          <div
            className="skeleton-pulse"
            style={{ width: '48%', height: 30, marginTop: 20, marginBottom: 16, borderRadius: 4, background: SHIMMER_MID }}
          />

          {/* moves */}
          <div className="movesScroll">
            <ol className="moveList">
              {[0, 1, 2].map((moveIndex) => (
                <li className="move" key={moveIndex}>
                  <div className="moveHeader">
                    <span className="moveMarker skeleton-pulse" />
                    <span
                      className="skeleton-pulse"
                      style={{ height: 14, width: 74, display: 'block', background: SHIMMER_MID }}
                    />
                    <span
                      className="skeleton-pulse"
                      style={{ height: 18, width: 62, borderRadius: 6, display: 'block', background: SHIMMER_DARK }}
                    />
                  </div>

                  <div className="moveCard">
                    <div className="moveCardHead">
                      <span className="moveIcon skeleton-pulse" style={{ background: SHIMMER_DARK }} />
                      <div style={{ flex: 1 }}>
                        <div className="skeleton-pulse" style={{ height: 16, width: '62%', background: SHIMMER_MID }} />
                        <div
                          className="skeleton-pulse"
                          style={{ height: 12, width: '90%', marginTop: 8, background: SHIMMER_DARK }}
                        />
                      </div>
                    </div>

                    {[0, 1].map((pointIndex) => (
                      <div key={pointIndex} style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <div
                          className="skeleton-pulse"
                          style={{ width: 4, height: 4, marginTop: 8, borderRadius: 999, flexShrink: 0, background: SHIMMER_DARK }}
                        />
                        <div style={{ flex: 1 }}>
                          <div className="skeleton-pulse" style={{ height: 13, width: '45%', background: SHIMMER_MID }} />
                          <div
                            className="skeleton-pulse"
                            style={{ height: 12, width: '78%', marginTop: 6, background: SHIMMER_DARK }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* ctaStack */}
          <div className="ctaStack">
            <div
              className="skeleton-pulse"
              style={{
                width: '100%',
                height: 55,
                borderRadius: 100,
                background: 'rgba(255,212,132,0.28)',
              }}
            />
            <div
              className="skeleton-pulse"
              style={{ width: '100%', height: 55, borderRadius: 100, background: SHIMMER_MID }}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
